import {
  Avatar,
  Box,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { IconEdit, IconShield } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { ProfileSkeleton } from "../../components/ui/SkeletonLoader";
import Text from "../../components/ui/Text";
import { useAuth } from "../../contexts/AuthContext";

const MyProfile = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [isLoading] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [isAddCreditModalOpen, setIsAddCreditModalOpen] = useState(false);

  // Show skeleton loader while loading
  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: "70px", pb: 4 }}>
        <ProfileSkeleton />
      </Container>
    );
  }

  const handleEditProfile = () => {
    navigate("/edit-profile");
  };

  const handleOpenAddCreditModal = () => {
    setIsAddCreditModalOpen(true);
  };

  const handleCloseAddCreditModal = () => {
    setIsAddCreditModalOpen(false);
    setCreditAmount("");
  };

  const handleAddGameCredit = () => {
    // TODO: Wire this to the new add payment method flow
    setIsAddCreditModalOpen(false);
  };

  const dateJoined = new Date(currentUser.created_at);
  const memberSince = `${dateJoined.toLocaleString("en-GB", { month: "short" })} ${dateJoined.getDate()}, ${dateJoined.getFullYear()}`;

  return (
    <Box sx={{ pt: "12px", display: "flex", flexDirection: "column" }}>
      <Container>
        {/* Header with title and edit button - responsive */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
          }}
        >
          <Text variant="pageHeader" sx={{ mb: 0 }}>
            My Profile
          </Text>
          {/* Edit Profile Button - hidden on mobile, shown on desktop */}
          <Button
            variant="link"
            startIcon={<IconEdit size={16} />}
            onClick={handleEditProfile}
            sx={{
              display: { xs: "none", md: "flex" },
            }}
          >
            Edit Profile
          </Button>
        </Box>

        {/* Profile Info Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar
              src={currentUser?.profile_image_url}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: "#1890FF",
                fontSize: "18px",
                fontWeight: 600,
                mb: "auto",
              }}
            >
              {currentUser.first_name[0].toUpperCase()}
              {currentUser?.last_name?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Text variant="subtitle3" sx={{ mb: "4px", display: "block" }}>
                {currentUser.first_name} {currentUser.last_name}
              </Text>
              <Text variant="paragraph3" sx={{ mb: "8px" }}>
                {currentUser.bio}
              </Text>
            </Box>
          </Box>

          {/* Edit Profile Button - shown on mobile only */}
          <Button
            variant="link"
            startIcon={<IconEdit size={16} />}
            onClick={handleEditProfile}
            sx={{
              display: { xs: "flex", md: "none" },
              my: 2,
            }}
          >
            Edit Profile
          </Button>
        </Box>
      </Container>

      <Container sx={{ backgroundColor: "#F5F5F5", p: "24px", flex: 1 }}>
        {/* Cards Container - responsive layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "16px", md: "24px" },
          }}
        >
          {/* Game Credit Card */}
          <Card
            sx={{
              flex: { xs: "none", md: 1 },
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
              borderRadius: "8px",
              mb: { xs: "16px", md: 0 },
              "&:last-child": { mb: 0 },
            }}
          >
            <CardContent
              sx={{
                p: "16px !important",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexDirection: { xs: "row", md: "column", lg: "row" },
                  gap: { xs: 0, md: 1, lg: 0 },
                }}
              >
                <Text variant="paragraph3">Game credit</Text>
                <Button
                  variant="link"
                  sx={{
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    minWidth: "auto",
                  }}
                  onClick={handleOpenAddCreditModal}
                >
                  Add game credit
                </Button>
              </Box>
              <Text
                variant="paragraph"
                sx={{ fontSize: { xs: "16px", md: "18px" } }}
              >
                {`€ ${currentUser.credits.toFixed(2)}`}
              </Text>
            </CardContent>
          </Card>

          {/* Member Since Card */}
          <Card
            sx={{
              flex: { xs: "none", md: 1 },
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
              borderRadius: "8px",
              mb: { xs: "16px", md: 0 },
              "&:last-child": { mb: 0 },
            }}
          >
            <CardContent
              sx={{
                p: "16px !important",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                height: "100%",
              }}
            >
              <Text variant="paragraph3">Member since</Text>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconShield size={20} color="#1890FF" />
                <Text
                  variant="paragraph"
                  sx={{ fontSize: { xs: "16px", md: "18px" } }}
                >
                  {memberSince}
                </Text>
              </Box>
            </CardContent>
          </Card>

          {/* Games Played Card */}
          <Card
            sx={{
              flex: { xs: "none", md: 1 },
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
              borderRadius: "8px",
              mb: { xs: "16px", md: 0 },
              "&:last-child": { mb: 0 },
            }}
          >
            <CardContent
              sx={{
                p: "16px !important",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                height: "100%",
              }}
            >
              <Text variant="paragraph3">Games played</Text>
              <Text
                variant="paragraph"
                sx={{ fontSize: { xs: "16px", md: "18px" } }}
              >
                {currentUser.total_games_played}
              </Text>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* Add Credit Modal */}
      <Dialog
        open={isAddCreditModalOpen}
        onClose={handleCloseAddCreditModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Text variant="paragraph" sx={{ fontWeight: 600 }}>
            Add Game Credit
          </Text>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Input
              label="Amount (€)"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              placeholder="0.00"
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button variant="secondary" onClick={handleCloseAddCreditModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddGameCredit}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyProfile;
